import dayjs from "dayjs"
import { orderBy, trim } from "lodash"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  STUDENT_TASK_TYPES,
  StudentTask,
  StudentTaskCardData,
} from "#customTypes/composedTypes/studentTaskTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus } from "#customTypes/types"

import { getNow, isInFuture } from "./dateHelpers"
import { ErrorType } from "./textFormatters"

export const MISSING_GUARDIAN_INFO_TASK_LABEL =
  "Missing guardian info — get guardian name, phone, and email from the parent."

type StudentGuardianFields = Partial<
  Pick<
    StudentConstructor,
    | "guardian1Name"
    | "guardian1Phone"
    | "guardian1Email"
    | "guardian2Name"
    | "guardian2Phone"
    | "guardian2Email"
  >
>

/**
 * A student has usable guardian info when at least one guardian has a name
 * AND a way to reach them (phone or email).
 */
export const hasGuardianContactInfo = (
  student: StudentGuardianFields
): boolean => {
  const hasGuardian1 =
    Boolean(trim(student.guardian1Name)) &&
    (Boolean(trim(student.guardian1Phone)) ||
      Boolean(trim(student.guardian1Email)))

  const hasGuardian2 =
    Boolean(trim(student.guardian2Name)) &&
    (Boolean(trim(student.guardian2Phone)) ||
      Boolean(trim(student.guardian2Email)))

  return hasGuardian1 || hasGuardian2
}

export const createMissingGuardianInfoTask = (
  createdBy: Uuid
): StudentTask => ({
  id: Date.now().toString(),
  label: MISSING_GUARDIAN_INFO_TASK_LABEL,
  type: STUDENT_TASK_TYPES.PARENT,
  isCompleted: false,
  createdAt: getNow().toISOString(),
  createdBy,
  completedAt: null,
  completedBy: null,
})

/** Tasks still waiting on the front desk (`isCompleted` is the canonical flag). */
export const getOpenTasks = (
  tasks: StudentTask[] | null | undefined
): StudentTask[] => (tasks ?? []).filter((task) => !task.isCompleted)

export const hasOpenTasks = (tasks: StudentTask[] | null | undefined) =>
  getOpenTasks(tasks).length > 0

/** Oldest open task shown on the roster task line; FIFO matches front-desk workflow. */
export const selectPrimaryOpenTask = (
  openTasks: StudentTask[]
): StudentTask | undefined => orderBy(openTasks, ["createdAt"], ["asc"])[0]

export const getIsAllTasksCompleted = (
  tasks: StudentTask[] | null | undefined
) => {
  if (!tasks || tasks.length === 0) return true

  return tasks.every((task) => task.isCompleted)
}

/**
 * Statuses meaning the student is physically in the building right now —
 * the moment the front desk can still act on their open tasks (JARVIS-69).
 * ENROLLED / ABSENT / PLANNED_ABSENCE variants are not "present".
 */
export const PRESENT_ATTENDANCE_STATUSES: ReadonlySet<AttendanceStatus> =
  new Set<AttendanceStatus>([
    ATTENDANCE_STATUSES.PRESENT,
    ATTENDANCE_STATUSES.TARDY,
    ATTENDANCE_STATUSES.MAKEUP_PRESENT,
    ATTENDANCE_STATUSES.MAKEUP_TARDY,
    ATTENDANCE_STATUSES.TRYOUT_PRESENT,
    ATTENDANCE_STATUSES.TRYOUT_TARDY,
  ])

export const isPresentStatus = (status: AttendanceStatus): boolean =>
  PRESENT_ATTENDANCE_STATUSES.has(status)

/**
 * Whether the roster task line is open. A manual flag toggle always wins; with
 * no toggle recorded the line is open exactly while the student is present —
 * so marking present/tardy auto-opens it on every machine (the attendance
 * status syncs), and a page reload changes nothing.
 */
export const getIsTaskLineOpen = (args: {
  status: AttendanceStatus
  tasks: StudentTask[] | null | undefined
  manualOpen: boolean | undefined
}): boolean =>
  hasOpenTasks(args.tasks) && (args.manualOpen ?? isPresentStatus(args.status))

/** studentId -> epoch ms (real clock) when they were last marked present. */
export type PresentHighlightsByStudentId = Record<Uuid, number>

const omitStudentIds = <T>(
  values: Record<Uuid, T>,
  studentIds: Uuid[]
): Record<Uuid, T> => {
  if (studentIds.length === 0) return values

  const next = { ...values }
  studentIds.forEach((id) => delete next[id])

  return next
}

/**
 * Display filter: only glow students who currently have an open card. A student
 * absent from `taskCards` is hidden but NOT forgotten, because absence is
 * ambiguous — a mid-load empty list looks identical to "nobody has tasks".
 */
export const selectActiveHighlights = (
  highlights: PresentHighlightsByStudentId,
  taskCards: StudentTaskCardData[]
): PresentHighlightsByStudentId => {
  const openCardStudentIds = new Set(
    taskCards
      .filter((card) => !card.isAllTasksCompleted)
      .map((card) => card.studentId)
  )

  return omitStudentIds(
    highlights,
    Object.keys(highlights).filter((id) => !openCardStudentIds.has(id))
  )
}

/**
 * State pruner: forget a highlight once we have positively observed that the
 * student's card is finished. Deliberately keyed on an *observed completed
 * card*, never on absence from the list — pruning on absence wipes real
 * arrivals every time a query restarts empty (e.g. changing the selected date).
 *
 * Forgetting matters as well as hiding: a retained timestamp would be reapplied
 * the moment a new task arrived for a student still marked present, glowing a
 * card that never had a fresh arrival.
 */
export const pruneCompletedHighlights = (
  highlights: PresentHighlightsByStudentId,
  taskCards: StudentTaskCardData[]
): PresentHighlightsByStudentId =>
  omitStudentIds(
    highlights,
    taskCards
      .filter(
        (card) => card.isAllTasksCompleted && card.studentId in highlights
      )
      .map((card) => card.studentId)
  )

/**
 * Forget local task-line flag toggles only after positively observing a
 * completed card. Absence from `taskCards` may only mean the task query is
 * still loading.
 */
export const pruneCompletedTaskLineToggles = (
  taskLineToggles: Record<Uuid, boolean>,
  taskCards: StudentTaskCardData[]
): Record<Uuid, boolean> =>
  omitStudentIds(
    taskLineToggles,
    taskCards
      .filter(
        (card) => card.isAllTasksCompleted && card.studentId in taskLineToggles
      )
      .map((card) => card.studentId)
  )

/**
 * How long the task card flashes after its student is marked present before
 * settling into the steady glow. Deliberately much shorter than the ticket's
 * "one minute" — a busy front desk screen with several cards flashing for a
 * minute each is noise, not signal. Tune here.
 */
export const PRESENT_FLASH_DURATION_MS = 6_000

export type PresentHighlightPhase = "flash" | "glow"

export const getPresentHighlightPhase = (
  markedPresentAt: number,
  nowMs: number,
  flashDurationMs: number = PRESENT_FLASH_DURATION_MS
): PresentHighlightPhase =>
  nowMs - markedPresentAt < flashDurationMs ? "flash" : "glow"

/**
 * Open cards first; among those, students just marked present float to the
 * top (most recent first), then urgent, then by name. Completed cards sink.
 */
export const sortTasks = (
  taskCards: StudentTaskCardData[],
  presentHighlights: PresentHighlightsByStudentId = {}
) => {
  return orderBy(
    taskCards,
    [
      (taskCard) => getIsAllTasksCompleted(taskCard.tasks),
      (taskCard) => presentHighlights[taskCard.studentId] ?? 0,
      (taskCard) => taskCard.isUrgent,
      (taskCard) => taskCard.header,
    ],
    ["asc", "desc", "desc", "asc"]
  )
}

/**
 * Determines if a student's tasks are urgent based on whether they're past the halfway point
 * of their total time at the facility for the day (from first session start to last session end).
 * This helps catch students before they leave for the day.
 *
 * `studentSessions` must be the student's actual sessions for the day (regular enrollment
 * + makeup sessions). Inferring sessions from `student.attendance` would miss makeup
 * students, since `MAKEUP_ENROLLED` is written to the missed class, not the makeup class.
 */
export const isTaskUrgent = (
  student: StudentConstructor,
  studentSessions: ClassSessionConstructor[],
  errors: ErrorType[]
): boolean => {
  if (studentSessions.length === 0) {
    errors.push(`${student.name} (${student.id}): No sessions found for today!`)
    return false
  }

  // Find the earliest start time and latest end time
  const sessionTimes = studentSessions.map((session) => ({
    start: dayjs(session.eventStart),
    end: dayjs(session.eventEnd),
  }))

  const earliestStart = sessionTimes.reduce(
    (earliest, current) =>
      current.start.isBefore(earliest) ? current.start : earliest,
    sessionTimes[0].start
  )

  const latestEnd = sessionTimes.reduce(
    (latest, current) => (current.end.isAfter(latest) ? current.end : latest),
    sessionTimes[0].end
  )

  const now = getNow()
  // Check if student is currently at the facility
  if (now.isBefore(earliestStart) || now.isAfter(latestEnd)) {
    return false
  }

  // Calculate halfway point of their total time at facility
  const totalDuration = latestEnd.diff(earliestStart)
  const halfwayPoint = earliestStart.add(totalDuration / 2, "millisecond")

  // Tasks are urgent if past halfway point of their total time
  return isInFuture(now, halfwayPoint)
}
